import moment from 'moment-timezone';
import { GetRowsRequest } from '../../grid/types/common';

export interface FilterKeyMapper {
  /**
   * Mapper to be used to map a column name to a different key when filtering.
   * By default the filter key is the column name. Use the mapper to override this.
   */
  [key: string]: string;
}

interface TimeRangeGridFilter {
  timeRange: {
    absolute?: AbsoluteTimeRange;
    relative?: RelativeTimeRange;
  };
}

interface RelativeTimeRange {
  lastNumDays: number;
}

interface AbsoluteTimeRange {
  fromTimestamp: number;
  toTimestamp: number;
}

interface QueryFiler {
  property: string;
  value: string | Array<string>;
}

enum FilterMapperKey {
  CONTAINS = 'contains',
  STARTS_WITH = 'startsWith',
  TIME_RANGE = 'timeRange',
  IN = 'in',
  VALUE = 'value',
  REGEX = 'regex',
}

interface StringFilterMapperPayload {
  contains: string;
  startsWith: string;
  value: string;
  regex: {
    pattern: string;
  };
}

export class GridRequestToConsolePayload {
  private dir: string | null;
  private limit: number;
  private page: number;
  private sort: string | null;
  private filter: Array<QueryFiler> | undefined;

  private filterMapper = {
    [FilterMapperKey.CONTAINS]: this.mapStringFilter,
    [FilterMapperKey.STARTS_WITH]: this.mapStringFilter,
    [FilterMapperKey.TIME_RANGE]: this.mapTimeFilter,
    [FilterMapperKey.IN]: this.mapEnumFilter,
    [FilterMapperKey.VALUE]: this.mapStringFilter,
    [FilterMapperKey.REGEX]: this.mapStringFilter,
  };

  constructor(gridRequest: GetRowsRequest<any, any>, timezone: string, filterKeyMapper?: FilterKeyMapper) {
    this.setPage(gridRequest);
    this.setFilters(gridRequest, timezone, filterKeyMapper);
    this.setSort(gridRequest);
  }

  public getPayload() {
    const payload: any = {};

    for (const key of Object.keys(this)) {
      const hasValue =
        (Array.isArray(this[key]) && this[key]?.length) || this[key] === 0 || (this[key] && key !== 'filterMapper');

      if (hasValue) {
        payload[key] = this[key];
      }
    }

    return payload;
  }

  private setPage({ pageSize, pageNumber }: GetRowsRequest<any, any>) {
    this.page = pageNumber!;
    this.limit = pageSize!;
  }

  private setFilters(gridRequest: GetRowsRequest<any, any>, timezone: string, filterKeyMapper: FilterKeyMapper = {}) {
    const filters: Array<QueryFiler> = (
      gridRequest.filters?.map(filter => {
        let filterKey = Object.keys(filter)[0];
        const filterValue = filter[filterKey];
        const filterValueKey = Object.keys(filter[filterKey])[0];
        const mapper = this.filterMapper[filterValueKey];

        filterKey = filterKeyMapper[filterKey] ?? filterKey;

        if (!mapper) {
          throw new Error(`No mapper for ${filterValueKey} in GridRequestToConsoleQuery`);
        }

        return mapper(filterKey, filterValue, timezone);
      }) as any
    )
      .flat()
      .filter(({ value }) => {
        if (Array.isArray(value) && !value.length) {
          return false;
        }

        return value || value === 0;
      });

    this.filter = filters;
  }

  private setSort({ sortColumnName, sortDirection }: GetRowsRequest<any, any>) {
    this.sort = sortDirection ? sortColumnName! : null;
    this.dir = sortDirection ? sortDirection.toUpperCase() : null;
  }

  private mapStringFilter(property: string, data: StringFilterMapperPayload): QueryFiler {
    const { contains, startsWith, value, regex } = data;

    return { property, value: contains ?? startsWith ?? value ?? regex?.pattern };
  }

  private mapEnumFilter(property: string, filterValues: { in: Array<string & number> }): Array<QueryFiler> {
    return [{ property, value: filterValues.in }];
  }

  private mapTimeFilter(property: string, value: TimeRangeGridFilter, timezone: string): Array<QueryFiler> {
    if (!value.timeRange) {
      return [{ property, value: '' }];
    }

    const format = 'YYYY-MM-DDTHH:mm:ss';
    const selectedFromTimestamp =
      value.timeRange?.absolute?.fromTimestamp || moment().subtract(value.timeRange.relative?.lastNumDays, 'days');
    const selectedToTimestamp = value.timeRange?.absolute?.toTimestamp || new Date();

    const fromTimestamp = moment(selectedFromTimestamp).tz(timezone).startOf('day');
    const toTimestamp = moment(selectedToTimestamp).tz(timezone).endOf('day');

    return [
      {
        property: `${property}[min]`,
        value: fromTimestamp.format(format),
      },
      {
        property: `${property}[max]`,
        value: toTimestamp.format(format),
      },
    ];
  }
}
